/** @jsx isolateComponent */
import React from 'react';
import { css } from '@emotion/core';

import { isolateComponent } from '@zapier/style-encapsulation';
import {
  Spacer,
  Surface as DesignSystemSurface,
  Typography,
  TypographyName,
} from '@zapier/design-system';

import Section from '../../shared/Section';
import { SizeType } from '../../../types';

type SurfaceProps = {
  /** Set to `false` to disable the shadow */
  hasShadow?: boolean;
  /** Optional title, which will push headings in `children` down one level */
  title?: string;
  /** Optional title typography, which will override the default style */
  titleTypography?: TypographyName;
  /** Children to render */
  children: React.ReactNode;
  /** Size to display. */
  size?: SizeType;
};

const contentCss = (size: SizeType) => css`
  padding: ${size === 'small' ? '20px 10px' : '30px 40px'};
  ${Typography.Body1}
`;

/**
 * The Design System `<Surface>` component, configured as we use it in Get Help.
 */
const Surface = ({
  children,
  hasShadow = true,
  size = 'large',
  title,
  titleTypography,
}: SurfaceProps) => {
  return (
    <DesignSystemSurface
      backgroundColor="transparent"
      borderColor="GrayWarm4"
      shadow={hasShadow ? 'elevation5' : undefined}
    >
      <div css={contentCss(size)}>
        {title ? (
          <Section size={size} title={title} titleTypography={titleTypography}>
            <Spacer height={10} />
            {children}
          </Section>
        ) : (
          children
        )}
      </div>
    </DesignSystemSurface>
  );
};

export default Surface;
